import { createElement, useEffect, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  GridCellProps,
  getColumnClasses,
  getRowClasses,
  getFlexProperties
} from "../lib";
import { getSpacing } from "@usb-ui-tools/react-billpay-theme";

const GridCell = ({
  row,
  col,
  tagName,
  addClasses,
  children,
  isGrid,
  removeFlex,
  styles,
  direction = null,
  wrap = null,
  alignContent = null,
  justifyContent = null,
  alignItems = null,
  rowGap,
  columnGap,
  ...otherProps
}: GridCellProps) => {
  const [columnClasses, setColumnClasses] = useState<string[]>([]);
  const [rowClasses, setRowClasses] = useState<string[]>([]);

  useEffect(() => {
    if (col) {
      setColumnClasses(getColumnClasses(col));
    }
  }, [col]);

  useEffect(() => {
    if (row) {
      setRowClasses(getRowClasses(row));
    }
  }, [row]);

  return createElement(
    `${tagName}`,
    {
      className: cx(
        "usbbp-grid-cell",
        {
          "sub-grid": isGrid,
          "remove-flex": removeFlex
        },
        ...columnClasses,
        ...rowClasses,
        ...addClasses
      ),
      style: {
        ...styles,
        ...getFlexProperties(
          direction,
          wrap,
          alignContent,
          justifyContent,
          alignItems
        ),
        rowGap: getSpacing(rowGap),
        columnGap: getSpacing(columnGap)
      },
      ...otherProps
    },
    children
  );
};

const layoutOpts = {
  max: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  xxl: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  xl: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  lg: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  md: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  sm: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  }),
  min: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number
  })
};

GridCell.propTypes = {
  row: PropTypes.shape(layoutOpts),
  col: PropTypes.shape(layoutOpts).isRequired,
  tagName: PropTypes.string,
  addClasses: PropTypes.arrayOf(PropTypes.string),
  isGrid: PropTypes.bool,
  removeFlex: PropTypes.bool,
  direction: PropTypes.oneOf([
    "RIGHT_TO_LEFT",
    "LEFT_TO_RIGHT",
    "TOP_TO_BOTTOM",
    "BOTTOM_TO_TOP"
  ]),
  wrap: PropTypes.oneOf(["WRAP", "NO_WRAP"]),
  alignContent: PropTypes.oneOf([
    "BASELINE",
    "CENTER",
    "FLEX_START",
    "FLEX_END",
    "SPACE_AROUND",
    "SPACE_BETWEEN",
    "SPACE_EVENLY",
    "STRETCH",
    "NORMAL",
    "UNSET",
    "INITIAL",
    "INHERIT"
  ]),
  justifyContent: PropTypes.oneOf([
    "BASELINE",
    "CENTER",
    "FLEX_START",
    "FLEX_END",
    "SPACE_AROUND",
    "SPACE_BETWEEN",
    "SPACE_EVENLY",
    "STRETCH",
    "NORMAL",
    "UNSET",
    "INITIAL",
    "INHERIT"
  ]),
  alignItems: PropTypes.oneOf([
    "BASELINE",
    "CENTER",
    "FLEX_START",
    "FLEX_END",
    "SELF_START",
    "SELF_END",
    "SPACE_AROUND",
    "SPACE_BETWEEN",
    "SPACE_EVENLY",
    "STRETCH",
    "NORMAL",
    "UNSET",
    "INITIAL",
    "INHERIT"
  ]),
  rowGap: PropTypes.string,
  columnGap: PropTypes.string,
  styles: PropTypes.shape({
    margin: PropTypes.string,
    padding: PropTypes.string,
    font: PropTypes.string,
    backgroundColor: PropTypes.string,
    backgroundImage: PropTypes.string,
    color: PropTypes.string,
    border: PropTypes.string,
    borderTop: PropTypes.string,
    borderRight: PropTypes.string,
    borderBottom: PropTypes.string,
    borderLeft: PropTypes.string,
    borderRadius: PropTypes.string,
    boxShadow: PropTypes.string,
    transition: PropTypes.string
  }),
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.array,
    PropTypes.object,
    PropTypes.node
  ])
};
GridCell.defaultProps = {
  row: null,
  tagName: "section",
  addClasses: [],
  isGrid: false,
  removeFlex: false,
  children: null,
  styles: {},
  direction: null,
  wrap: null,
  alignContent: null,
  justifyContent: null,
  alignItems: null,
  rowGap: "X0",
  columnGap: "X0"
};

export default GridCell;
